import { Text } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useEffect, type FC, useState, useRef, useCallback, useMemo } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { CopyableTextArea } from "@/components/podkit/forms/CopyableTextArea";
import { useCreateRunnerExchangeToken } from "@/queries/runner-queries";
import { Check, InfoIcon } from "lucide-react";
import { Button } from "@/components/flexkit/Button";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { toast } from "@/components/podkit/toasts/use-toast";
import { GCPRunnerDetails } from "@/components/runners/details/GCPRunnerDetails";

export const GCPRunnerSetup: FC<{ runner: PlainMessage<Runner>; hasSignedUp: boolean }> = ({ runner, hasSignedUp }) => {
    return (
        <div className="flex flex-col gap-4">
            {hasSignedUp ? <GCPRunnerDetailsSection runner={runner} /> : <GCPRunnerCreate runner={runner} />}
        </div>
    );
};

export const GCPRunnerCreate: FC<{ runner: PlainMessage<Runner> }> = ({ runner }) => {
    const createRunnerExchangeToken = useCreateRunnerExchangeToken();
    const [exchangeToken, setExchangeToken] = useState<string>();
    const [generated, setGenerated] = useTemporaryState(false, 2000);
    const calledOnce = useRef(false);

    const generateToken = useCallback(
        async (fromButton: boolean) => {
            try {
                const result = await createRunnerExchangeToken.mutateAsync(runner.runnerId);
                setExchangeToken(result.exchangeToken);
                if (fromButton) {
                    setGenerated(true);
                }
            } catch (error) {
                console.error("Failed to generate exchange token", error);
                toast({
                    title: "Failed to generate token",
                    description: "Please try again later",
                });
            }
        },
        [createRunnerExchangeToken, runner.runnerId, setGenerated],
    );

    useEffect(() => {
        calledOnce.current = false;
    }, [runner.runnerId]);

    useEffect(() => {
        if (calledOnce.current) {
            return;
        }
        calledOnce.current = true;

        void generateToken(false);
    }, [runner.runnerId, createRunnerExchangeToken, generateToken]);

    const apiEndpoint = useMemo(() => {
        const isGitpodIO = window.location.origin === "https://app.gitpod.io";
        if (isGitpodIO) {
            return "https://app.gitpod.io/api";
        }
        return `${window.location.origin}/api`;
    }, []);

    const terraformVarsContent = useMemo(() => {
        if (!exchangeToken) return "# Loading...";

        // Format runner name for terraform (lowercase, replace spaces/special chars with hyphens)
        const formattedRunnerName = runner.name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens

        return `# Auto-generated values, do not change these
api_endpoint = "${apiEndpoint}"
runner_id    = "${runner.runnerId}"
runner_token = "${exchangeToken}"

# Required variables
project_id = ""
region     = "us-central1"
zones      = ["us-central1-a", "us-central1-b", "us-central1-c"]

runner_name   = "${formattedRunnerName}"

runner_domain = ""
certificate_id = "" #  certificate ID in the format "projects/project-name/locations/global/certificates/cert-name" with runner_domain and *.runner_domain SANs

runner_subnet_name = "" # subnet in which the runner will be deployed`;
    }, [exchangeToken, runner.runnerId, runner.name, apiEndpoint]);

    return (
        <div className="flex flex-col gap-6 pb-4">
            <div className="rounded-lg bg-surface-secondary">
                <Text className="text-sm text-content-secondary">
                    Use Terraform to deploy your GCP runner infrastructure. Follow these steps to get started.
                </Text>
            </div>

            <div className="flex flex-col divide-y divide-border-base">
                {/* Step 1: Clone Repository */}
                <SetupStep
                    number={1}
                    title="Clone Terraform Repository"
                    description="Get the official Ona GCP Terraform configuration"
                >
                    <CopyableTextArea
                        className="font-mono text-sm"
                        copyable
                        value={`git clone https://github.com/gitpod-io/gitpod-gcp-terraform.git
cd gitpod-gcp-terraform`}
                        title="Copy command"
                        data-tracking-id="copy-clone-terraform-repo-gcp-runner-setup"
                    />
                </SetupStep>

                {/* Step 2: Configure Variables */}
                <SetupStep
                    number={2}
                    title="Create terraform.tfvars"
                    description="Configure your runner with the generated credentials"
                >
                    <div className="flex flex-col gap-3">
                        <CopyableTextArea
                            className={cn(
                                "font-mono text-sm",
                                (!exchangeToken || createRunnerExchangeToken.isPending) && "animate-pulse opacity-50",
                            )}
                            copyable={!!exchangeToken && !createRunnerExchangeToken.isPending}
                            value={terraformVarsContent}
                            title="Copy terraform.tfvars content"
                            data-tracking-id="copy-terraform-vars-gcp-runner-setup"
                        />
                        <div className="flex items-center justify-between gap-2">
                            <Text className="text-xs text-content-tertiary">
                                Token expires in 24 hours, or after one-time use
                            </Text>
                            <Button
                                variant="ghost"
                                type="button"
                                size="sm"
                                className="flex items-center gap-2"
                                loading={createRunnerExchangeToken.isPending}
                                LeadingIcon={() => (generated ? <Check className="text-green-500" size={12} /> : null)}
                                onClick={() => generateToken(true)}
                                data-tracking-id="generate-new-token-gcp-runner-setup"
                            >
                                Generate new token
                            </Button>
                        </div>
                        <div className="flex items-start gap-2 rounded-lg bg-surface-secondary p-3">
                            <div className="mt-0.5 text-amber-500">
                                <InfoIcon size={16} />
                            </div>
                            <div>
                                <Text className="text-sm font-medium text-content-primary">
                                    Save this as terraform.tfvars in your project directory
                                </Text>
                                <Text className="text-sm text-content-secondary">
                                    Fill in the required variables: project_id (your GCP project ID), runner_domain
                                    (your custom domain for the runner), certificate_id (SSL certificate ID), and
                                    runner_subnet_name (subnet for runner deployment).
                                </Text>
                            </div>
                        </div>
                    </div>
                </SetupStep>

                {/* Step 3: Deploy Infrastructure */}
                <SetupStep
                    number={3}
                    title="Deploy with Terraform"
                    description="Initialize and apply your Terraform configuration"
                >
                    <div className="flex flex-col gap-3">
                        <CopyableTextArea
                            copyable
                            className="font-mono text-sm"
                            value={`terraform init
terraform plan
terraform apply`}
                            title="Copy commands"
                            data-tracking-id="copy-terraform-apply-gcp-runner-setup"
                        />
                        <div className="flex items-start gap-2 rounded-lg bg-surface-secondary p-3">
                            <div className="mt-0.5 text-blue-500">
                                <InfoIcon size={16} />
                            </div>
                            <div>
                                <Text className="text-sm font-medium text-content-primary">
                                    Review the plan before applying
                                </Text>
                                <Text className="text-sm text-content-secondary">
                                    Make sure you understand the resources that will be created in your GCP project.
                                </Text>
                            </div>
                        </div>
                    </div>
                </SetupStep>
            </div>

            <div className="flex items-center gap-3">
                <ExternalLink
                    href="https://github.com/gitpod-io/gitpod-gcp-terraform"
                    iconSize="sm"
                    className="text-sm"
                >
                    View Repository
                </ExternalLink>
                <Text className="text-sm text-content-secondary">•</Text>
                <ExternalLink href="https://ona.com/docs/ona/runners/gcp/overview" iconSize="sm" className="text-sm">
                    Documentation
                </ExternalLink>
            </div>
        </div>
    );
};

const GCPRunnerDetailsSection: FC<{ runner: PlainMessage<Runner> }> = ({ runner }) => {
    return (
        <div className="flex flex-col gap-4">
            <GCPRunnerDetails runner={runner} />

            <div className="flex items-center gap-3">
                <ExternalLink
                    href="https://github.com/gitpod-io/gitpod-gcp-terraform"
                    iconSize="sm"
                    className="text-sm"
                >
                    Terraform Repository
                </ExternalLink>
                <ExternalLink href="https://ona.com/docs/ona/runners/gcp/overview" iconSize="sm" className="text-sm">
                    Documentation
                </ExternalLink>
            </div>
        </div>
    );
};

const SetupStep: FC<{
    number: number;
    title: string;
    description: string;
    children: React.ReactNode;
}> = ({ number, title, description, children }) => (
    <div className="flex flex-col gap-2 py-4 first:pt-0 last:pb-4">
        <div className="flex items-center gap-3">
            <Text className="font-medium">
                {number}. {title}
            </Text>
        </div>
        <Text className="text-sm text-content-secondary">{description}</Text>
        <div>{children}</div>
    </div>
);
